import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import AccountService from '../../../services/AccountService';

/**
 * RelatedTickets component displays tickets related to the account
 */
function RelatedTickets({ accountId }) {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);

  useEffect(() => {
    const fetchRelatedTickets = async () => {
      if (!accountId) return;
      
      setLoading(true);
      try {
        const response = await AccountService.getAccountRelatedTickets(accountId);
        setTickets(response.data || []);
        setError(null);
      } catch (err) {
        console.error('Error fetching related tickets:', err);
        setError('Failed to load related tickets. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchRelatedTickets();
  }, [accountId]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewDetails = (ticket) => {
    setSelectedTicket(ticket);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusChip = (status) => {
    let color = 'default';
    
    switch(status?.toLowerCase()) {
      case 'open':
      case 'in progress':
        color = 'primary';
        break;
      case 'pending':
      case 'waiting':
        color = 'warning';
        break;
      case 'closed':
      case 'completed':
      case 'resolved':
        color = 'success';
        break;
      case 'cancelled':
        color = 'error';
        break;
      default:
        color = 'default';
    }
    
    return <Chip label={status} color={color} size="small" />;
  };

  const getPriorityChip = (priority) => {
    let color = 'default';
    
    switch(priority?.toLowerCase()) {
      case 'high':
      case 'urgent':
        color = 'error';
        break;
      case 'medium':
        color = 'warning';
        break;
      case 'low':
        color = 'success';
        break;
      default:
        color = 'default';
    }
    
    return <Chip label={priority} color={color} size="small" />;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (tickets.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">No related tickets found for this account.</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Related Tickets
      </Typography>
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 440 }}>
          <Table stickyHeader aria-label="related tickets table">
            <TableHead>
              <TableRow>
                <TableCell>Ticket ID</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Created Date</TableCell>
                <TableCell>Last Updated</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tickets
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((ticket) => (
                  <TableRow hover key={ticket.id}>
                    <TableCell>{ticket.id}</TableCell>
                    <TableCell>{ticket.title}</TableCell>
                    <TableCell>{getStatusChip(ticket.status)}</TableCell>
                    <TableCell>{getPriorityChip(ticket.priority)}</TableCell>
                    <TableCell>{formatDate(ticket.createdDate)}</TableCell>
                    <TableCell>{formatDate(ticket.lastUpdated)}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          size="small"
                          onClick={() => handleViewDetails(ticket)}
                        >
                          View Details
                        </Button>
                        <Button
                          size="small"
                          component={RouterLink}
                          to={`/ADM-Ticket/ticket-details/${ticket.id}`}
                        >
                          Go to Ticket
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={tickets.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Ticket Details Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        aria-labelledby="ticket-details-dialog-title"
        maxWidth="md"
        fullWidth
      >
        <DialogTitle id="ticket-details-dialog-title">
          Ticket Details
        </DialogTitle>
        <DialogContent dividers>
          {selectedTicket && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2">Ticket ID:</Typography>
                <Typography>{selectedTicket.id}</Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2">Title:</Typography>
                <Typography>{selectedTicket.title}</Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2">Status:</Typography>
                <Typography>{getStatusChip(selectedTicket.status)}</Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2">Priority:</Typography>
                <Typography>{getPriorityChip(selectedTicket.priority)}</Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2">Created Date:</Typography>
                <Typography>{formatDate(selectedTicket.createdDate)}</Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2">Last Updated:</Typography>
                <Typography>{formatDate(selectedTicket.lastUpdated)}</Typography>
              </Box>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="subtitle2">Description:</Typography>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="body2">{selectedTicket.description || 'No description available'}</Typography>
                </Paper>
              </Box>
              
              {selectedTicket.assignedTo && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="subtitle2">Assigned To:</Typography>
                  <Typography>{selectedTicket.assignedTo}</Typography>
                </Box>
              )}
              
              {selectedTicket.relatedInvoiceId && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="subtitle2">Related Invoice:</Typography>
                  <Link 
                    component={RouterLink} 
                    to={`/wallet/invoice-details/${selectedTicket.relatedInvoiceId}`}
                  >
                    {selectedTicket.relatedInvoiceId}
                  </Link>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
          {selectedTicket && (
            <Button 
              component={RouterLink} 
              to={`/ADM-Ticket/ticket-details/${selectedTicket.id}`}
              color="primary"
            >
              Go to Ticket
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default RelatedTickets;
